/*
 * newsbot - PayloadPreview component for webhook actions
 */

import { useState } from "react";
import { DocumentDuplicateIcon, CheckIcon } from "@heroicons/react/24/outline";
import { classNames } from "@utils";

interface PayloadPreviewProps {
  actionType: "webhook_n8n" | "webhook_cuppa";
}

const SAMPLE_N8N_PAYLOAD = {
  story_id: "01HMEXAMPLE123456789ABCDEF",
  headline: "Breaking: Major Development in Tech Industry",
  summary: "A comprehensive summary of the breaking news story covering key developments...",
  category_id: "tech",
  category_name: "Technology",
  importance_score: 8.5,
  tags: ["technology", "innovation", "breaking-news"],
  content_type: "news",
  suggested_titles: [
    "Major Tech Development Shakes Industry Amid Market Uncertainty",
    "Breaking: Tech Giants Face New Challenges As Innovation Accelerates",
  ],
  project: null,
};

const SAMPLE_CUPPA_PAYLOAD = {
  story_id: "01HMEXAMPLE123456789ABCDEF",
  headline: "Breaking: Major Development in Tech Industry",
  summary: "A comprehensive summary of the breaking news story covering key developments...",
  category: "Technology",
  category_slug: "technology",
  importance_score: 8.5,
  tags: ["technology", "innovation", "breaking-news"],
  article_count: 12,
  source_domains: ["techcrunch.com", "theverge.com", "wired.com"],
  first_seen_at: "2025-08-17T10:30:00Z",
  content_type: "promotional",
  suggested_titles: [
    "BMIC Enters the Crypto Presale Market With Post-Quantum Wallet Vision Amid Security Concerns",
    "$BMIC Presale Goes Live! Post-Quantum Wallet Claims to Future-Proof Your Crypto",
  ],
  project: {
    project_name: "BMIC",
    ticker: "$BMIC",
    chain: "Ethereum",
    value_prop: "Post-quantum resistant wallet infrastructure",
    raise_amount: "$2.3M",
    presale_stage: "public presale",
    comparison_hook: "addresses quantum computing threat to existing wallets",
    key_claims: ["quantum-resistant", "EVM compatible", "hardware wallet integration"],
  },
};

export function PayloadPreview({ actionType }: PayloadPreviewProps) {
  const [copied, setCopied] = useState(false);

  const samplePayload = actionType === "webhook_cuppa" ? SAMPLE_CUPPA_PAYLOAD : SAMPLE_N8N_PAYLOAD;
  const formattedJson = JSON.stringify(samplePayload, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <pre className={classNames(
          "rounded-md border p-4 overflow-x-auto text-sm font-mono",
          "bg-gray-100 text-gray-700 border-gray-200",
          "dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700"
        )}>
          {formattedJson}
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          className={classNames(
            "absolute top-2 right-2 p-2 rounded-md transition-colors",
            "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
            "hover:bg-gray-50 dark:hover:bg-gray-700",
            "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          )}
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? (
            <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : (
            <DocumentDuplicateIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          )}
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        This is the default payload structure. Use the template above to customize.
      </p>
    </div>
  );
}
